import React, { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Save, Upload, Download, X, KeyRound, ShieldCheck, RefreshCw, Database, FileSpreadsheet, Image as ImageIcon, Trash2 } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const canManage = user?.role === "admin";
  const [data, setData] = useState(null);
  const [newDept, setNewDept] = useState("");
  const [newPlant, setNewPlant] = useState({ name: "", address: "" });
  const [pwd, setPwd] = useState({ level1: "", level2: "" });
  const fileRef = useRef(null);
  const [normalizing, setNormalizing] = useState(false);

  const refreshAndNormalize = async () => {
    if (!window.confirm("This will scan all data tables, dedupe spelling/case duplicates (e.g. CONSUMABLE / Consumable / consumable → Consumable) and propagate canonical names everywhere. Continue?")) return;
    setNormalizing(true);
    try {
      const { data: r } = await api.post("/maintenance/normalize-data");
      const stats = r.report || {};
      const summary = `Normalized: ${stats.items_updated || 0} items · ${stats.indents_updated || 0} indents · ${stats.pos_updated || 0} POs · ${stats.store_entries_updated || 0} store entries · ${stats.inventory_merged || 0} inventory rows merged`;
      toast.success(summary, { duration: 5000 });
      // Force a clean refresh of the whole app so cached data is rebuilt
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Normalize failed");
    } finally {
      setNormalizing(false);
    }
  };

  const backupJsonRef = useRef(null);
  const backupExcelRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const downloadBackup = async () => {
    if (busy) return;
    setBusy(true);
    const t = toast.loading("Preparing full backup…");
    try {
      const res = await api.get("/backup/download", { responseType: "blob" });
      const today = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `CPMS_Backup_${today}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup downloaded", { id: t });
    } catch (err) {
      // Server returned an error as a Blob — read it as text to surface the real reason
      let msg = "Backup failed";
      try {
        const blob = err?.response?.data;
        if (blob && typeof blob.text === "function") {
          const txt = await blob.text();
          try { msg = JSON.parse(txt).detail || msg; }
          catch { if (txt) msg = txt; }
        } else if (err?.message) {
          msg = err.message;
        }
      } catch { /* keep default msg */ }
      const status = err?.response?.status;
      if (status === 401) msg = "Session expired — please log in again";
      if (status === 502 || status === 503 || status === 504) msg = "Server warming up — try again in a few seconds";
      toast.error(msg, { id: t });
    } finally {
      setBusy(false);
    }
  };

  const restoreFromJson = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm(`Restoring from "${file.name}" will REPLACE all current data with the backup contents. Continue?`)) return;
    setBusy(true);
    const t = toast.loading("Restoring backup…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/backup/restore", fd);
      const total = Object.values(data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restored ${total} rows across ${Object.keys(data.restored || {}).length} collections`, { id: t });
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const restoreFromExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm(`Re-importing masters from "${file.name}" will REPLACE the current Item/Supplier/Intender/Category/Cost Center/Required/Department/Process masters. Continue?`)) return;
    setBusy(true);
    const t = toast.loading("Re-importing from Excel…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/backup/restore-excel", fd);
      const total = Object.values(data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Imported ${total} rows from Excel`, { id: t });
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Excel restore failed", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const adoptDefaultData = async () => {
    if (!window.confirm(
      "Recover all data from the original 'default' company workspace into your current account?\n\n" +
      "Use this if your items, indents, POs etc. are missing after signing in via Swami Hub SSO. " +
      "It moves every row from the default tenant to your current company. Safe to run — it does NOT delete anything."
    )) return;
    setBusy(true);
    const t = toast.loading("Recovering default-company data…");
    try {
      const { data } = await api.post("/maintenance/adopt-default-data");
      if (data.skipped === "already_on_default") {
        toast.info("You are already on the default tenant — no migration needed.", { id: t });
        return;
      }
      const total = Object.values(data.moved || {}).reduce((a, b) => a + b, 0);
      toast.success(`Recovered ${total} rows`, { id: t });
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Recovery failed", { id: t });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    api.get("/settings").then((r) => setData(r.data));
  }, []);

  const save = async () => {
    if (!canManage) return;
    try {
      const { data: updated } = await api.put("/settings", {
        company_name: data.company_name,
        company_address: data.company_address,
        company_gst: data.company_gst,
        company_contact: data.company_contact,
        departments: data.departments,
        plants: data.plants,
        required_for_options: data.required_for_options,
        invoice_terms: data.invoice_terms || "",
        msme_no: data.msme_no || "",
        company_email: data.company_email || "",
        bank_details: data.bank_details || "",
        place_of_supply: data.place_of_supply || "",
      });
      setData(updated);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const addDept = () => {
    if (!newDept.trim()) return;
    if (data.departments.includes(newDept.trim())) return toast.error("Department already exists");
    setData({ ...data, departments: [...data.departments, newDept.trim()] });
    setNewDept("");
  };
  const removeDept = (d) => setData({ ...data, departments: data.departments.filter((x) => x !== d) });

  const addPlant = () => {
    if (!newPlant.name.trim()) return toast.error("Plant name required");
    if ((data.plants || []).some((p) => p.name === newPlant.name.trim())) return toast.error("Plant already exists");
    setData({ ...data, plants: [...(data.plants || []), { name: newPlant.name.trim(), address: newPlant.address.trim() }] });
    setNewPlant({ name: "", address: "" });
  };
  const removePlant = (n) => setData({ ...data, plants: (data.plants || []).filter((p) => p.name !== n) });
  const editPlantAddress = (n, addr) => setData({ ...data, plants: data.plants.map((p) => p.name === n ? { ...p, address: addr } : p) });

  const downloadTemplate = async () => {
    const token = localStorage.getItem("pms_token");
    const r = await fetch(`${API}/settings/template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "departments_template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data: updated } = await api.post("/settings/import-departments", fd);
    setData((d) => ({ ...d, departments: updated.departments }));
    toast.success(`Imported ${updated.departments.length} departments`);
  };

  const saveApprovalPasswords = async () => {
    const body = {};
    if (pwd.level1.trim()) body.level1_password = pwd.level1.trim();
    if (pwd.level2.trim()) body.level2_password = pwd.level2.trim();
    if (!body.level1_password && !body.level2_password) {
      return toast.error("Enter at least one password to update");
    }
    try {
      const { data: res } = await api.put("/settings/approval-passwords", body);
      toast.success(`Updated: ${res.updated.join(", ")}`);
      setPwd({ level1: "", level2: "" });
      const { data: fresh } = await api.get("/settings");
      setData(fresh);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const logoRef = useRef(null);
  const sigRef = useRef(null);
  const onUploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 1_500_000) return toast.error("Logo too large (max 1.5 MB)");
    const fd = new FormData();
    fd.append("file", file);
    const t = toast.loading("Uploading logo…");
    try {
      const { data: res } = await api.post("/settings/logo", fd);
      setData((d) => ({ ...d, company_logo: res.company_logo }));
      toast.success("Logo uploaded — will appear on Invoice & PO prints", { id: t });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed", { id: t });
    }
  };
  const onRemoveLogo = async () => {
    if (!window.confirm("Remove the current company logo?")) return;
    try {
      await api.delete("/settings/logo");
      setData((d) => ({ ...d, company_logo: "" }));
      toast.success("Logo removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  const onUploadSignature = async (file) => {
    if (!file) return;
    if (file.size > 1_000_000) return toast.error("Signature too large (max 1 MB)");
    const fd = new FormData();
    fd.append("file", file);
    const t = toast.loading("Uploading signature…");
    try {
      const { data: res } = await api.post("/settings/signature", fd);
      setData((d) => ({ ...d, e_signature: res.e_signature }));
      toast.success("E-Signature uploaded", { id: t });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed", { id: t });
    }
  };
  const onRemoveSignature = async () => {
    if (!window.confirm("Remove the current e-signature?")) return;
    try {
      await api.delete("/settings/signature");
      setData((d) => ({ ...d, e_signature: "" }));
      toast.success("E-Signature removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  if (!data) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] bg-white disabled:bg-slate-50 disabled:text-slate-500";

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500">System</div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Company information, plants and departments used across the workspace.</p>
      </div>

      {canManage && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 flex items-start justify-between gap-4 flex-wrap" data-testid="data-refresh-card">
          <div>
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <RefreshCw size={14} className="text-[#1D2951]" /> Refresh &amp; Normalize Data
            </div>
            <div className="text-xs text-slate-500 mt-1 max-w-3xl">
              Cleans up duplicates caused by manual data-entry inconsistencies (e.g. <span className="font-mono">CONSUMABLE</span> /
              <span className="font-mono"> Consumable</span> / <span className="font-mono">consumable</span> become a single canonical
              <span className="font-mono"> Consumable</span>). Title-cases category, make, department, required, process and cost-center
              names, then propagates the canonical values across Items, Indents, POs, Store Entries and the Store/Inward Log. Also
              merges inventory rows that point to the same item.
            </div>
          </div>
          <button
            onClick={refreshAndNormalize}
            disabled={normalizing}
            data-testid="settings-refresh-btn"
            className="bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 whitespace-nowrap"
          >
            <RefreshCw size={14} className={normalizing ? "animate-spin" : ""} />
            {normalizing ? "Normalizing…" : "Refresh Now"}
          </button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-6 grid grid-cols-2 gap-4">
        <div className="col-span-2 text-sm font-semibold text-slate-900">Company Information</div>
        <div><Lbl>Company Name</Lbl><input data-testid="settings-company-name" disabled={!canManage} value={data.company_name} onChange={(e) => setData({ ...data, company_name: e.target.value })} className={inputCls} /></div>
        <div><Lbl>GST Number</Lbl><input disabled={!canManage} value={data.company_gst} onChange={(e) => setData({ ...data, company_gst: e.target.value })} className={`${inputCls} font-mono`} /></div>
        <div className="col-span-2"><Lbl>Address</Lbl><textarea disabled={!canManage} value={data.company_address} onChange={(e) => setData({ ...data, company_address: e.target.value })} className={`${inputCls} min-h-[70px]`} /></div>
        <div><Lbl>Contact</Lbl><input disabled={!canManage} value={data.company_contact} onChange={(e) => setData({ ...data, company_contact: e.target.value })} className={inputCls} /></div>
        <div className="col-span-2"><Lbl>Invoice Terms &amp; Conditions (optional)</Lbl>
          <textarea disabled={!canManage} value={data.invoice_terms || ""} onChange={(e) => setData({ ...data, invoice_terms: e.target.value })}
            className={`${inputCls} min-h-[60px] text-xs`} placeholder="e.g. Payment within 30 days · Goods once sold not returnable" />
        </div>

        <div className="col-span-2 border-t border-slate-100 pt-4">
          <Lbl>Company Logo (appears on Tax Invoice &amp; PO prints)</Lbl>
          <div className="flex items-center gap-4 mt-1">
            <div className="w-28 h-28 border border-dashed border-slate-300 rounded-md flex items-center justify-center bg-slate-50 overflow-hidden">
              {data.company_logo ? (
                <img src={data.company_logo} alt="Logo" data-testid="company-logo-preview" className="w-full h-full object-contain" />
              ) : (
                <ImageIcon size={28} className="text-slate-300" />
              )}
            </div>
            {canManage && (
              <div className="flex flex-col gap-2">
                <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" hidden
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUploadLogo(f); }} />
                <button onClick={() => logoRef.current?.click()} data-testid="upload-logo-btn"
                  className="border border-[#1D2951]/30 text-[#1D2951] hover:bg-[#1D2951] hover:text-white px-3 py-1.5 rounded-md text-sm font-semibold inline-flex items-center gap-2">
                  <Upload size={14} /> {data.company_logo ? "Replace Logo" : "Upload Logo"}
                </button>
                {data.company_logo && (
                  <button onClick={onRemoveLogo} data-testid="remove-logo-btn"
                    className="text-rose-600 hover:bg-rose-50 px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-2">
                    <Trash2 size={12} /> Remove
                  </button>
                )}
                <div className="text-[11px] text-slate-400">PNG / JPG / SVG · max 1.5 MB · ~200×200 recommended</div>
              </div>
            )}
          </div>
        </div>

        <div className="col-span-2 border-t border-slate-100 pt-4 grid grid-cols-2 gap-4">
          <div><Lbl>Company Email</Lbl><input disabled={!canManage} value={data.company_email || ""} onChange={(e) => setData({ ...data, company_email: e.target.value })} className={inputCls} placeholder="info@yourcompany.com" /></div>
          <div><Lbl>MSME No.</Lbl><input disabled={!canManage} value={data.msme_no || ""} onChange={(e) => setData({ ...data, msme_no: e.target.value })} className={`${inputCls} font-mono`} placeholder="UDYAM-XX-00-0000000" /></div>
          <div className="col-span-2"><Lbl>Place of Supply (default)</Lbl><input disabled={!canManage} value={data.place_of_supply || ""} onChange={(e) => setData({ ...data, place_of_supply: e.target.value })} className={inputCls} placeholder="Maharashtra (27)" /></div>
          <div className="col-span-2"><Lbl>Bank Details (printed on the Tax Invoice)</Lbl>
            <textarea disabled={!canManage} value={data.bank_details || ""} onChange={(e) => setData({ ...data, bank_details: e.target.value })}
              className={`${inputCls} min-h-[60px] text-xs`} placeholder="IDBI BANK LTD — BHOSARI MCCIA / AC NO. xxxxxxxxxxxxxx / IFSC — IDKL0001678" />
          </div>

          {/* E-Signature upload */}
          <div className="col-span-2">
            <Lbl>Authorised Signatory (E-Signature)</Lbl>
            <div className="flex items-center gap-4 mt-1">
              <div className="w-40 h-20 border border-dashed border-slate-300 rounded-md flex items-center justify-center bg-slate-50 overflow-hidden">
                {data.e_signature ? (
                  <img src={data.e_signature} alt="Signature" data-testid="signature-preview" className="w-full h-full object-contain" />
                ) : (
                  <ImageIcon size={22} className="text-slate-300" />
                )}
              </div>
              {canManage && (
                <div className="flex flex-col gap-2">
                  <input ref={sigRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" hidden
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUploadSignature(f); }} />
                  <button onClick={() => sigRef.current?.click()} data-testid="upload-signature-btn"
                    className="border border-[#1D2951]/30 text-[#1D2951] hover:bg-[#1D2951] hover:text-white px-3 py-1.5 rounded-md text-sm font-semibold inline-flex items-center gap-2">
                    <Upload size={14} /> {data.e_signature ? "Replace Signature" : "Upload Signature"}
                  </button>
                  {data.e_signature && (
                    <button onClick={onRemoveSignature} data-testid="remove-signature-btn"
                      className="text-rose-600 hover:bg-rose-50 px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-2">
                      <Trash2 size={12} /> Remove
                    </button>
                  )}
                  <div className="text-[11px] text-slate-400">Transparent PNG works best · ~400×120</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {canManage && (
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="approval-passwords-card">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound size={16} className="text-[#1D2951]" />
            <div className="text-sm font-semibold text-slate-900">Approval Passwords</div>
            <span className="text-[10px] uppercase tracking-wider bg-[#1D2951]/5 text-[#1D2951] px-2 py-0.5 rounded font-semibold">Admin only</span>
          </div>
          <div className="text-xs text-slate-500 mb-4">
            These passwords are required on the Approve / Reject dialog. Leave blank to keep the existing value — type a new value to rotate it.
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Lbl>Level 1 Password (≤ ₹1000 · GM)</Lbl>
                <StatusChip ok={data.has_level1_password} />
              </div>
              <input
                type="password"
                data-testid="settings-l1-pwd"
                value={pwd.level1}
                onChange={(e) => setPwd({ ...pwd, level1: e.target.value })}
                className={inputCls}
                placeholder="Enter new Level 1 password"
                autoComplete="new-password"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Lbl>Level 2 Password (&gt; ₹1000 · CEO/MD)</Lbl>
                <StatusChip ok={data.has_level2_password} />
              </div>
              <input
                type="password"
                data-testid="settings-l2-pwd"
                value={pwd.level2}
                onChange={(e) => setPwd({ ...pwd, level2: e.target.value })}
                className={inputCls}
                placeholder="Enter new Level 2 password"
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={saveApprovalPasswords}
              data-testid="settings-save-approval-pwd"
              className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2"
            >
              <Save size={14} /> Update Approval Passwords
            </button>
          </div>
        </div>
      )}

      {/* Plants */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">Plant Management</div>
            <div className="text-xs text-slate-500">Each plant has a name + address. Plants appear in the Indent Sheet.</div>
          </div>
        </div>

        {canManage && (
          <div className="flex flex-wrap gap-2 mb-3">
            <input data-testid="settings-new-plant-name" value={newPlant.name} onChange={(e) => setNewPlant({ ...newPlant, name: e.target.value })} placeholder="Plant name (e.g. Unit 5)" className={`${inputCls} flex-1 min-w-[180px]`} />
            <input value={newPlant.address} onChange={(e) => setNewPlant({ ...newPlant, address: e.target.value })} placeholder="Address" className={`${inputCls} flex-[2] min-w-[260px]`} />
            <button onClick={addPlant} data-testid="settings-add-plant-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-3 py-2 rounded-md text-sm inline-flex items-center gap-2 font-semibold whitespace-nowrap"><Plus size={14} /> Add Plant</button>
          </div>
        )}

        <div className="space-y-2">
          {(data.plants || []).length === 0 && <div className="text-xs text-slate-400">No plants configured</div>}
          {(data.plants || []).map((p) => (
            <div key={p.name} className="flex items-center gap-2 border border-slate-200 rounded-md p-2">
              <div className="font-mono font-semibold w-24 text-sm">{p.name}</div>
              <input
                disabled={!canManage}
                value={p.address}
                onChange={(e) => editPlantAddress(p.name, e.target.value)}
                className={`${inputCls} flex-1`}
              />
              {canManage && <button onClick={() => removePlant(p.name)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"><X size={14} /></button>}
            </div>
          ))}
        </div>
      </div>

      {/* Departments */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">Departments</div>
            <div className="text-xs text-slate-500">Used in Indent Sheet and Issue Slips.</div>
          </div>
          <div className="flex gap-2">
            <button onClick={downloadTemplate} className="border border-slate-300 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2"><Download size={14} /> Template</button>
            {canManage && (
              <>
                <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) { onImport(f); e.target.value = ""; } }} />
                <button onClick={() => fileRef.current?.click()} className="border border-slate-300 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2"><Upload size={14} /> Import</button>
              </>
            )}
          </div>
        </div>

        {canManage && (
          <div className="flex gap-2 mb-3">
            <input data-testid="settings-new-dept-input" value={newDept} onChange={(e) => setNewDept(e.target.value)} placeholder="e.g. Logistics" className={inputCls} />
            <button onClick={addDept} data-testid="settings-add-dept-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-3 py-2 rounded-md text-sm inline-flex items-center gap-2 font-semibold whitespace-nowrap"><Plus size={14} /> Add</button>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {data.departments.length === 0 && <span className="text-xs text-slate-400">No departments</span>}
          {data.departments.map((d) => (
            <span key={d} className="text-sm bg-slate-100 px-3 py-1.5 rounded-md inline-flex items-center gap-2">
              {d}
              {canManage && <button onClick={() => removeDept(d)} className="text-rose-600 hover:text-rose-800"><X size={12} /></button>}
            </span>
          ))}
        </div>
      </div>

      {canManage && (
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="backup-restore-section">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-md bg-[#1D2951]/10 flex items-center justify-center">
              <Database size={16} className="text-[#1D2951]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Backup & Restore</h3>
              <p className="text-xs text-slate-500">Download a full snapshot, or restore from a JSON backup or an Excel export.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <button
              onClick={downloadBackup}
              disabled={busy}
              data-testid="backup-download-btn"
              className="border border-[#1D2951]/30 rounded-md p-4 text-left hover:bg-[#1D2951] hover:text-white transition-colors disabled:opacity-60 group"
            >
              <Download size={16} className="mb-2 text-[#1D2951] group-hover:text-white" />
              <div className="text-sm font-semibold">Download Full Backup</div>
              <div className="text-[11px] text-slate-500 group-hover:text-white/70 mt-1">JSON snapshot · every collection · ready for restore</div>
            </button>

            <button
              onClick={() => backupJsonRef.current?.click()}
              disabled={busy}
              data-testid="backup-restore-json-btn"
              className="border border-emerald-500/40 rounded-md p-4 text-left hover:bg-emerald-600 hover:text-white transition-colors disabled:opacity-60 group"
            >
              <Upload size={16} className="mb-2 text-emerald-700 group-hover:text-white" />
              <div className="text-sm font-semibold">Restore From Backup</div>
              <div className="text-[11px] text-slate-500 group-hover:text-white/70 mt-1">Upload a previously-downloaded CPMS_Backup_*.json</div>
              <input ref={backupJsonRef} type="file" accept=".json,application/json" hidden onChange={restoreFromJson} />
            </button>

            <button
              onClick={() => backupExcelRef.current?.click()}
              disabled={busy}
              data-testid="backup-restore-excel-btn"
              className="border border-amber-500/40 rounded-md p-4 text-left hover:bg-amber-600 hover:text-white transition-colors disabled:opacity-60 group"
            >
              <FileSpreadsheet size={16} className="mb-2 text-amber-700 group-hover:text-white" />
              <div className="text-sm font-semibold">Restore From Export Excel</div>
              <div className="text-[11px] text-slate-500 group-hover:text-white/70 mt-1">Upload a CPMS_Full_Export_*.xlsx · re-imports masters</div>
              <input ref={backupExcelRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={restoreFromExcel} />
            </button>
          </div>

          <div className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            ⚠️ Restoring is destructive — it replaces existing data. Download a fresh backup first if you want a rollback point.
          </div>

          <div className="mt-3 border-t border-slate-100 pt-3">
            <div className="text-xs text-slate-500 mb-2">Missing all your items / indents / POs after signing in via Swami Hub SSO?</div>
            <button
              onClick={adoptDefaultData}
              disabled={busy}
              data-testid="adopt-default-btn"
              className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-md border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors disabled:opacity-60 inline-flex items-center gap-2"
            >
              <RefreshCw size={12} /> Recover Original Company Data
            </button>
          </div>
        </div>
      )}

      {canManage && (
        <div className="flex justify-end">
          <button onClick={save} data-testid="settings-save-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2"><Save size={14} /> Save Settings</button>
        </div>
      )}
    </div>
  );
}

const Lbl = ({ children }) => (<div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{children}</div>);

function StatusChip({ ok }) {
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold inline-flex items-center gap-1 ${ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
      <ShieldCheck size={10} /> {ok ? "Configured" : "Not set"}
    </span>
  );
}
